/**
 * Collection of transition types for page animations
 */
export const TRANSITIONS = {
  FADE: "fade",
  SLIDE_UP: "slide-up",
  SLIDE_DOWN: "slide-down",
  SLIDE_LEFT: "slide-left",
  SLIDE_RIGHT: "slide-right",
  ZOOM_IN: "zoom-in",
  ZOOM_OUT: "zoom-out",
  NONE: "none",
};

/**
 * Map of route paths to transition types
 * This determines which animation is used when navigating to a specific route
 */
export const ROUTE_TRANSITIONS = {
  "/": TRANSITIONS.FADE,
  "/about": TRANSITIONS.ZOOM_IN,
  "/photography": TRANSITIONS.SLIDE_LEFT,
  "/media": TRANSITIONS.SLIDE_UP,
  "/contact": TRANSITIONS.ZOOM_IN,
};

/**
 * Map of route relationships to determine transition directions
 * Based on site hierarchy: home → about/photography/media → specific items
 */
const ROUTE_HIERARCHY = {
  "/": 0,
  "/about": 1,
  "/photography": 1,
  "/media": 1,
  "/contact": 0,
};

/**
 * Get the appropriate transition type based on current and previous routes
 * @param {string} from - Previous route path
 * @param {string} to - Current route path
 * @returns {string} - Transition type to use
 */
export const getRouteTransition = (from, to) => {
  // First navigation or no previous path
  if (!from) {
    return ROUTE_TRANSITIONS[to] || TRANSITIONS.FADE;
  }

  // Special case for contact route
  if (to === "/contact") {
    return TRANSITIONS.ZOOM_IN;
  }

  // Get hierarchy levels to determine direction
  const fromLevel = ROUTE_HIERARCHY[from] || 0;
  const toLevel = ROUTE_HIERARCHY[to] || 0;

  // Moving up in the hierarchy (deeper into the site)
  if (toLevel > fromLevel) {
    if (to === "/about") {
      return TRANSITIONS.ZOOM_IN;
    } else if (to === "/photography") {
      return TRANSITIONS.SLIDE_LEFT;
    } else if (to === "/media") {
      return TRANSITIONS.SLIDE_UP;
    }
    return TRANSITIONS.ZOOM_IN;
  }

  // Moving down in the hierarchy (back toward home)
  if (toLevel < fromLevel) {
    if (from === "/about") {
      return TRANSITIONS.ZOOM_OUT;
    } else if (from === "/photography") {
      return TRANSITIONS.SLIDE_RIGHT;
    } else if (from === "/media") {
      return TRANSITIONS.SLIDE_DOWN;
    }
    return TRANSITIONS.ZOOM_OUT;
  }

  // Same level - use lateral movement for transitions between sections
  if (from === "/about" && to === "/photography") {
    return TRANSITIONS.SLIDE_LEFT;
  } else if (from === "/about" && to === "/media") {
    return TRANSITIONS.SLIDE_UP;
  } else if (from === "/photography" && to === "/about") {
    return TRANSITIONS.SLIDE_RIGHT;
  } else if (from === "/photography" && to === "/media") {
    return TRANSITIONS.SLIDE_UP;
  } else if (from === "/media" && to === "/about") {
    return TRANSITIONS.SLIDE_DOWN;
  } else if (from === "/media" && to === "/photography") {
    return TRANSITIONS.SLIDE_DOWN;
  }

  // Default to the configured transition
  return ROUTE_TRANSITIONS[to] || TRANSITIONS.FADE;
};

/**
 * Map CSS classes for different transition stages
 * @param {string} transitionType - Type of transition
 * @param {string} stage - Current stage ('fadeIn' or 'fadeOut')
 * @returns {string} - CSS class for the transition
 */
export const getTransitionClass = (transitionType, stage) => {
  const isEntering = stage === "fadeIn";

  switch (transitionType) {
    case TRANSITIONS.SLIDE_UP:
      return isEntering ? "slide-up-enter" : "slide-up-exit";
    case TRANSITIONS.SLIDE_DOWN:
      return isEntering ? "slide-down-enter" : "slide-down-exit";
    case TRANSITIONS.SLIDE_LEFT:
      return isEntering ? "slide-left-enter" : "slide-left-exit";
    case TRANSITIONS.SLIDE_RIGHT:
      return isEntering ? "slide-right-enter" : "slide-right-exit";
    case TRANSITIONS.ZOOM_IN:
      return isEntering ? "zoom-in-enter" : "zoom-in-exit";
    case TRANSITIONS.ZOOM_OUT:
      return isEntering ? "zoom-out-enter" : "zoom-out-exit";
    case TRANSITIONS.NONE:
      return "";
    case TRANSITIONS.FADE:
    default:
      return isEntering ? "fade-enter" : "fade-exit";
  }
};
